import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ABOUT_INFO, SKILLS } from '../../../utils/constants';
import styles from './CommandPrompt.module.css';

interface HistoryEntry {
  command: string;
  output: string[];
}

const HELP_TEXT = [
  'Available commands:',
  '',
  '  help        - Display this help message',
  '  cls         - Clear the screen',
  '  clear       - Clear the screen',
  '  echo [text] - Display text',
  '  date        - Display current date',
  '  time        - Display current time',
  '  dir         - List directory contents',
  '  cd          - Change directory (simulated)',
  '  about       - Display information about the developer',
  '  skills      - List technical skills',
  '  contact     - Show contact information',
  '  projects    - List portfolio projects',
  '  ver         - Display Windows version',
  '  color [xx]  - Change console colors',
  '  exit        - Close Command Prompt',
  '',
  'Type a command and press Enter.',
];

const ASCII_ART = [
  '    _    ____ ___ _______   ___    ',
  '   / \\  |  _ \\_ _|_   _\\ \\ / / \\   ',
  '  / _ \\ | | | | |  | |  \\ V / _ \\  ',
  ' / ___ \\| |_| | |  | |   | / ___ \\ ',
  '/_/   \\_\\____/___| |_|   |_/_/   \\_\\',
  '',
];

export const CommandPrompt: React.FC = () => {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [currentPath, setCurrentPath] = useState('C:\\Users\\Guest');
  const [colorScheme, setColorScheme] = useState({ bg: '#000000', fg: '#C0C0C0' });

  const inputRef = useRef<HTMLInputElement>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // Focus input on mount and when clicking the terminal
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const scrollToBottom = useCallback(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [history, scrollToBottom]);

  const processCommand = useCallback((input: string): string[] => {
    const trimmed = input.trim();
    const parts = trimmed.split(' ');
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1).join(' ');

    switch (cmd) {
      case '':
        return [];

      case 'help':
      case '/?':
        return HELP_TEXT;

      case 'cls':
      case 'clear':
        setHistory([]);
        return [];

      case 'echo':
        return [args || ''];

      case 'date':
        return [`The current date is: ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`];

      case 'time':
        return [`The current time is: ${new Date().toLocaleTimeString()}`];

      case 'ver':
        return [
          '',
          'Microsoft Windows XP [Version 5.1.2600]',
          '(C) Copyright 1985-2001 Microsoft Corp.',
          '',
          '(Just kidding - this is a React portfolio by Aditya Kanteti)',
          '',
        ];

      case 'dir':
        return [
          ` Volume in drive C is PORTFOLIO`,
          ` Volume Serial Number is AD17-Y4K3`,
          '',
          ` Directory of ${currentPath}`,
          '',
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}    <DIR>          .`,
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}    <DIR>          ..`,
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}    <DIR>          Projects`,
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}    <DIR>          Documents`,
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}             1,337  resume.pdf`,
          `${new Date().toLocaleDateString()}  ${new Date().toLocaleTimeString()}               420  readme.txt`,
          `               2 File(s)          1,757 bytes`,
          `               4 Dir(s)   42,069,420,420 bytes free`,
        ];

      case 'cd':
        if (!args || args === '.') {
          return [currentPath];
        } else if (args === '..') {
          const pathParts = currentPath.split('\\');
          if (pathParts.length > 1) {
            setCurrentPath(pathParts.slice(0, -1).join('\\') || 'C:');
          }
          return [];
        } else if (args.toLowerCase() === 'projects') {
          setCurrentPath(currentPath + '\\Projects');
          return [];
        } else if (args.toLowerCase() === 'documents') {
          setCurrentPath(currentPath + '\\Documents');
          return [];
        } else {
          return [`The system cannot find the path specified.`];
        }

      case 'about':
        return [
          ...ASCII_ART,
          `Name: ${ABOUT_INFO.name}`,
          `Title: ${ABOUT_INFO.title}`,
          `Location: ${ABOUT_INFO.location}`,
          '',
          ...ABOUT_INFO.bio.split('\n'),
          '',
        ];

      case 'skills':
        const skillsByCategory: Record<string, typeof SKILLS> = {};
        SKILLS.forEach(skill => {
          if (!skillsByCategory[skill.category]) {
            skillsByCategory[skill.category] = [];
          }
          skillsByCategory[skill.category].push(skill);
        });

        const categoryLabels: Record<string, string> = {
          frontend: 'FRONTEND',
          backend: 'BACKEND & LANGUAGES',
          ml: 'ML & DATA',
          tools: 'INFRASTRUCTURE & DEVOPS',
        };

        const output: string[] = ['', 'Technical Skills:', ''];
        Object.entries(skillsByCategory).forEach(([category, skills]) => {
          output.push(`  ${categoryLabels[category] || category.toUpperCase()}:`);
          skills.forEach(skill => {
            const bar = '='.repeat(Math.floor(skill.level / 10)) + '-'.repeat(10 - Math.floor(skill.level / 10));
            output.push(`    ${skill.name.padEnd(15)} [${bar}] ${skill.level}%`);
          });
          output.push('');
        });
        return output;

      case 'contact':
        return [
          '',
          'Contact Information:',
          '',
          `  Email:    ${ABOUT_INFO.email}`,
          `  Location: ${ABOUT_INFO.location}`,
          '',
          '  GitHub:   github.com/IJustWantAJob',
          '  Twitter:  twitter.com/AdiCodeMonkey',
          '  LinkedIn: linkedin.com/in/Adityakanteti',
          '',
          'Type "projects" to see my work!',
          '',
        ];

      case 'projects':
        return [
          '',
          'Portfolio Projects:',
          '',
          '  [FEATURED]',
          '  1. Wattnest - Smart Industrial Controllers    wattnest.net',
          '     React, TypeScript, Python, CTA-2045, Modbus, AWS',
          '',
          '  2. Kanteti.me - Personal Website              kanteti.me',
          '     Next.js 15, Three.js, Anime.js, Tailwind CSS',
          '',
          '  3. SUA Parliamentary Assistant                suaparli.org',
          '     Next.js 15, Letta AI, Vercel AI SDK, Google APIs',
          '',
          '  4. Windows XP Portfolio (You are here!)',
          '     React 18, TypeScript, Vite, CSS Modules, Web Audio',
          '',
          '  [OTHER PROJECTS]',
          '  5. Voxelize Personal World                    game.kanteti.me',
          '  6. WebGL Education Playground                 webgl.kanteti.me',
          '  7. Load Shifting Optimization Research (HPWH)',
          '  8. Quantum Computing Research (QAOA/Qiskit)',
          '  9. USPS Occupancy Regression (ML Pipeline)',
          '',
          'Visit the Projects app for more details!',
          '',
        ];

      case 'color':
        if (!args || args.length !== 2) {
          return [
            'Sets the default console foreground and background colors.',
            '',
            'COLOR [attr]',
            '',
            '  attr - Specifies color attribute of console output',
            '',
            'Color attributes are specified by TWO hex digits:',
            '  0 = Black    8 = Gray',
            '  1 = Blue     9 = Light Blue',
            '  2 = Green    A = Light Green',
            '  3 = Aqua     B = Light Aqua',
            '  4 = Red      C = Light Red',
            '  5 = Purple   D = Light Purple',
            '  6 = Yellow   E = Light Yellow',
            '  7 = White    F = Bright White',
            '',
            'Example: COLOR 0A for green text on black',
          ];
        }

        const colorMap: Record<string, string> = {
          '0': '#000000', '1': '#000080', '2': '#008000', '3': '#008080',
          '4': '#800000', '5': '#800080', '6': '#808000', '7': '#C0C0C0',
          '8': '#808080', '9': '#0000FF', 'A': '#00FF00', 'B': '#00FFFF',
          'C': '#FF0000', 'D': '#FF00FF', 'E': '#FFFF00', 'F': '#FFFFFF',
        };

        const bgChar = args[0].toUpperCase();
        const fgChar = args[1].toUpperCase();

        if (colorMap[bgChar] && colorMap[fgChar]) {
          setColorScheme({ bg: colorMap[bgChar], fg: colorMap[fgChar] });
          return [];
        }
        return ['Invalid color code'];

      case 'exit':
        // This would need to be handled by the window system
        return ['Type the X button to close this window.'];

      default:
        return [
          `'${cmd}' is not recognized as an internal or external command,`,
          `operable program or batch file.`,
        ];
    }
  }, [currentPath]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    const output = processCommand(currentInput);

    if (currentInput.trim().toLowerCase() !== 'cls' && currentInput.trim().toLowerCase() !== 'clear') {
      setHistory(prev => [...prev, { command: currentInput, output }]);
    }

    if (currentInput.trim()) {
      setCommandHistory(prev => [...prev, currentInput]);
    }

    setCurrentInput('');
    setHistoryIndex(-1);
  }, [currentInput, processCommand]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        setCurrentInput(commandHistory[commandHistory.length - 1 - newIndex] || '');
      } else if (historyIndex === 0) {
        setHistoryIndex(-1);
        setCurrentInput('');
      }
    }
  }, [commandHistory, historyIndex]);

  const handleContainerClick = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div
      className={styles.terminal}
      style={{ backgroundColor: colorScheme.bg, color: colorScheme.fg }}
      onClick={handleContainerClick}
    >
      <div className={styles.output} ref={outputRef}>
        <div className={styles.welcome}>
          Microsoft Windows XP [Version 5.1.2600]
          <br />
          (C) Copyright 1985-2001 Microsoft Corp.
          <br />
          <br />
          Welcome to Aditya's Portfolio Terminal!
          <br />
          Type 'help' for available commands.
          <br />
          <br />
        </div>

        {history.map((entry, index) => (
          <div key={index} className={styles.historyEntry}>
            <div className={styles.commandLine}>
              <span className={styles.prompt}>{currentPath}&gt;</span>
              <span>{entry.command}</span>
            </div>
            {entry.output.map((line, lineIndex) => (
              <div key={lineIndex} className={styles.outputLine}>{line}</div>
            ))}
          </div>
        ))}

        <form onSubmit={handleSubmit} className={styles.inputLine}>
          <span className={styles.prompt}>{currentPath}&gt;</span>
          <input
            ref={inputRef}
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.input}
            style={{ color: colorScheme.fg }}
            autoComplete="off"
            spellCheck={false}
          />
        </form>
      </div>
    </div>
  );
};

export default CommandPrompt;
